import { useState, useEffect } from 'react'
import { ArrowRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, FileText, Minus } from 'lucide-react'
import type { PreReadDiff } from '../lib/diff/preReadDiff'

interface Props {
  projectId: string
  basePreReadId: string
  targetPreReadId: string
}

function ConfidenceBand({ band }: { band: string }) {
  const colors: Record<string, string> = {
    High: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    Medium: 'text-amber-700 bg-amber-50 border-amber-200',
    Low: 'text-red-700 bg-red-50 border-red-200',
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-medium ${colors[band] ?? 'text-gray-600 bg-gray-50 border-gray-200'}`}>
      {band}
    </span>
  )
}

function DeltaBadge({ delta }: { delta: number }) {
  const pct = Math.round(Math.abs(delta) * 100)
  if (delta > 0.01) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
        <TrendingUp className="w-3 h-3" /> +{pct}pp
      </span>
    )
  }
  if (delta < -0.01) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-600">
        <TrendingDown className="w-3 h-3" /> -{pct}pp
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-gray-400">
      <Minus className="w-3 h-3" /> Stable
    </span>
  )
}

export function PreReadDiffPage({ projectId, basePreReadId, targetPreReadId }: Props) {
  const [diff, setDiff] = useState<(PreReadDiff & { diff_id?: string }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDiff = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await fetch(`/api/v1/projects/${projectId}/pre-reads/diff`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base_pre_read_id: basePreReadId,
            target_pre_read_id: targetPreReadId,
          }),
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json() as PreReadDiff & { diff_id?: string }
        setDiff(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to compute diff')
      } finally {
        setLoading(false)
      }
    }
    fetchDiff()
  }, [projectId, basePreReadId, targetPreReadId])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      </div>
    )
  }

  if (!diff) return null

  const hasChanges =
    diff.winner_changes.length > 0 ||
    diff.confidence_changes.length > 0 ||
    diff.dissent_changes.length > 0 ||
    diff.evidence_added.length > 0 ||
    diff.validation_added.length > 0

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-1">
          <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{basePreReadId}</span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{targetPreReadId}</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Pre-Read Diff</h1>
      </div>

      {/* Why summary */}
      <div className={`p-4 rounded-xl border text-sm leading-relaxed ${hasChanges ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
        <div className="flex items-start gap-2">
          {hasChanges
            ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            : <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />}
          <p>{diff.why_summary}</p>
        </div>
      </div>

      {/* Winner changes */}
      {diff.winner_changes.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Winner Flips ({diff.winner_changes.length})
          </h2>
          <div className="space-y-2">
            {diff.winner_changes.map(wc => (
              <div key={wc.segment_id} className="bg-white border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">{wc.segment_id}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500 line-through">{wc.base_winner}</span>
                    <ArrowRight className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold text-echo-indigo">{wc.target_winner}</span>
                  </div>
                </div>
                <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                  FLIPPED
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Confidence changes */}
      {diff.confidence_changes.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-echo-indigo" />
            Confidence Changes ({diff.confidence_changes.length})
          </h2>
          <div className="space-y-2">
            {diff.confidence_changes.map(cc => (
              <div key={cc.segment_id} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-800">{cc.segment_id}</span>
                  <DeltaBadge delta={cc.delta} />
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>Base: <strong className="text-gray-800">{Math.round(cc.base_confidence * 100)}%</strong></span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
                  <span>Target: <strong className="text-gray-800">{Math.round(cc.target_confidence * 100)}%</strong></span>
                </div>
                {cc.drivers_changed.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {cc.drivers_changed.map(d => (
                      <span key={d.driver} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {d.driver}: {Math.round(d.base * 100)}% → {Math.round(d.target * 100)}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Dissent changes */}
      {diff.dissent_changes.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            Dissent Changes ({diff.dissent_changes.length})
          </h2>
          <div className="space-y-2">
            {diff.dissent_changes.map(dc => (
              <div key={dc.segment_id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-gray-800">{dc.segment_id}</p>
                {dc.new_dissent_notes.map(n => (
                  <div key={n} className="flex items-start gap-2 text-xs text-orange-700 bg-orange-50 rounded px-2 py-1">
                    <span className="font-semibold shrink-0">NEW</span> {n}
                  </div>
                ))}
                {dc.resolved_dissent_notes.map(n => (
                  <div key={n} className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                    <span className="font-semibold shrink-0">RESOLVED</span> {n}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Evidence changes */}
      {(diff.evidence_added.length > 0 || diff.evidence_removed.length > 0) && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-echo-indigo" />
            Evidence Changes
          </h2>
          <div className="space-y-2">
            {diff.evidence_added.map(e => (
              <div key={e.upload_id} className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-semibold text-emerald-600 text-xs">ADDED</span>
                <span className="font-mono text-gray-600 text-xs">{e.upload_id}</span>
              </div>
            ))}
            {diff.evidence_removed.map(e => (
              <div key={e.upload_id} className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-semibold text-red-600 text-xs">REMOVED</span>
                <span className="font-mono text-gray-600 text-xs">{e.upload_id}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Validation added */}
      {diff.validation_added.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-echo-indigo" />
            Validation Added ({diff.validation_added.length})
          </h2>
          <div className="space-y-2">
            {diff.validation_added.map(v => (
              <div key={v.job_id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm">
                <p className="font-mono text-xs text-gray-500 mb-1">{v.job_id}</p>
                <p className="text-gray-700">{v.outcomes_summary}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {!hasChanges && (
        <div className="text-center py-12 text-gray-400">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No material differences between these two pre-reads.</p>
        </div>
      )}
    </div>
  )
}
